import { dependencies, version } from '../../pkgInfo.json';
import platform from 'platform';
import PerformanceUtils from '../PerformanceUtils';
import ProfileStorage from '../../personalization/ProfileStorage';
import GeoService from '../../services/GeoService';

type ScreenMeasurement = {
	colorDepth: number;
	pixelDepth: number;
	height: number;
	width: number;
	availHeight: number;
	availWidth: number;
};
export default class LoggerPayload {
	hostContext: HostContext;
	userProfileInfo: UserProfileInformation = {
		entitlements: [],
		fulfilledEntitlements: [],
		subscription: {},
	};
	geoData: GeoData = {
		country: '',
		state: '',
		'Accept-Language': '',
	};
	static instance: LoggerPayload;

	constructor(hostContext: HostContext) {
		if (!LoggerPayload.instance) {
			this.hostContext = hostContext;
			// try to get user profile information
			const userProf = ProfileStorage.readProfilesResponseFromSessionStorage();

			// if we have a user profile information use it
			if (userProf) {
				this.userProfileInfo = {
					entitlements: userProf.entitlements,
					fulfilledEntitlements: userProf.fulfilledEntitlements,
					subscription: userProf.subscription,
				};
			}

			LoggerPayload.instance = this;
		}

		return LoggerPayload.instance;
	}

	// Returns the payload
	getPayload(payloadComponentName: string, payloadMessage: string, payloadData?: any): Record<string, any> {
		return {
			searchVersion: version,
			searchDependencies: this.getDependencies(),
			locale: this.getLanguageByLocale(this.hostContext.locale),
			location: this.geoData,
			surface: this.hostContext.hostInfo.surface,
			client: this.hostContext.hostInfo.client,
			userProfileInformation: this.userProfileInfo,
			adobeUser: this.isAdobeUser(),
			adobeTestUser: this.isAdobeTestUser(),
			browserPerformanceData: PerformanceUtils.getPerformanceData(),
			browserHistoryCount: window.history ? window.history.length : 0,
			screenMeasurement: this.getScreenMeasurement(),
			componentName: `CCSearch: ${payloadComponentName}`,
			message: payloadMessage,
			data: payloadData ? payloadData : 'No data available',
			browser: {
				name: platform.name,
				description: platform.description,
				userAgent: platform.ua,
				version: platform.version,
				os: {
					architecture: platform.os.architecture,
					family: platform.os.family,
					version: platform.os.version,
				},
			},
		};
	}

	// Returns the dependencies of CCSearch
	getDependencies(): Record<string, any> {
		// Filter out Non-Adobe modules
		const filteredDependencies = Object.keys(dependencies)
			.filter((key) => key.startsWith('@'))
			.reduce((obj, key) => {
				obj[key] = dependencies[key];
				return obj;
			}, {});
		return filteredDependencies;
	}

	// Returns the language for the locale code
	getLanguageByLocale(locale: string): string {
		const localeToLanguage = {
			en: 'English',
			ja: 'Japanese',
			de: 'German',
			fr: 'French',
			ko: 'Korean',
			it: 'Italian',
			es: 'Spanish',
			pt: 'Brazilian Portuguese',
			da: 'Danish',
			nb: 'Norwegian',
			nl: 'Dutch',
			sv: 'Swedish',
			// eslint-disable-next-line camelcase
			zh_Hans: 'Chinese Simplified',
			// eslint-disable-next-line camelcase
			zh_Hant: 'Chinese Traditional (HK, TW)',
			ru: 'Russian',
			fi: 'Finnish',
			pl: 'Polish',
			uk: 'Ukrainian',
			cs: 'Czech',
			hu: 'Hungarian',
			tr: 'Turkish',
		};
		let languageByLocale = 'Unknown';

		if (localeToLanguage[locale]) {
			languageByLocale = localeToLanguage[locale];
		}

		return languageByLocale;
	}

	// Returns true if it's an Adobe user
	isAdobeUser(): boolean {
		return this.hostContext.imsInfo.email && this.hostContext.imsInfo.email.split('@')[1] === 'adobe.com';
	}

	// Returns true if it's an Adobe Test user
	isAdobeTestUser(): boolean {
		return this.hostContext.imsInfo.email && this.hostContext.imsInfo.email.split('@')[1] === 'adobetest.com';
	}

	// Returns the screen measurement
	getScreenMeasurement(): ScreenMeasurement {
		const screenMeasurement = {
			colorDepth: 0,
			pixelDepth: 0,
			height: 0,
			width: 0,
			availHeight: 0,
			availWidth: 0,
		};

		if (window && window.screen) {
			window.screen.colorDepth ? (screenMeasurement.colorDepth = window.screen.colorDepth) : 0;
			window.screen.pixelDepth ? (screenMeasurement.pixelDepth = window.screen.pixelDepth) : 0;
			window.screen.height ? (screenMeasurement.height = window.screen.height) : 0;
			window.screen.width ? (screenMeasurement.width = window.screen.width) : 0;
			window.screen.availHeight ? (screenMeasurement.availHeight = window.screen.availHeight) : 0;
			window.screen.availWidth ? (screenMeasurement.availWidth = window.screen.availWidth) : 0;
		}

		return screenMeasurement;
	}
}
